import { useEffect, useRef } from "react";

const CENTERS = [
  [270, 52],  [164, 88],  [376, 88],
  [88, 168],  [204, 136], [270, 146], [336, 136], [452, 168],
  [52, 270],  [162, 238], [270, 248], [378, 238], [488, 270],
  [88, 372],  [204, 348], [270, 358], [336, 348], [452, 372],
  [184, 462], [270, 474], [356, 462],
];

const CONNECT_DIST = 142;

const BrainViz = ({ className = "hero-brain" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const W = 540, H = 540;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const nodes = CENTERS.map(([cx, cy]) => ({
      cx, cy, x: cx, y: cy,
      rx: 8 + Math.random() * 18,
      ry: 8 + Math.random() * 18,
      phaseX:    Math.random() * Math.PI * 2,
      phaseY:    Math.random() * Math.PI * 2,
      freqX:     0.00018 + Math.random() * 0.00012,
      freqY:     0.00022 + Math.random() * 0.00012,
      r:         1.8 + Math.random() * 2.2,
      glowPhase: Math.random() * Math.PI * 2,
    }));

    let raf = null;
    let isVisible = true;

    const startLoop = () => {
      if (!raf) raf = requestAnimationFrame(frame);
    };

    const stopLoop = () => {
      if (raf) { cancelAnimationFrame(raf); raf = null; }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { isVisible = true; startLoop(); }
        else { isVisible = false; stopLoop(); }
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    const frame = (t) => {
      raf = null;
      if (!isVisible) return;

      ctx.clearRect(0, 0, W, H);

      nodes.forEach(n => {
        n.x = n.cx + Math.sin(t * n.freqX + n.phaseX) * n.rx;
        n.y = n.cy + Math.sin(t * n.freqY + n.phaseY) * n.ry;
      });

      ctx.save();
      ctx.shadowColor = "rgba(96,165,250,0.85)";
      ctx.shadowBlur = 9;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            const alpha = 1 - d / CONNECT_DIST;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(96,165,250,${(alpha * 0.65).toFixed(3)})`;
            ctx.lineWidth = alpha * 1.5;
            ctx.stroke();
          }
        }
      }
      ctx.restore();

      ctx.save();
      ctx.shadowColor = "rgba(96,165,250,0.55)";
      ctx.shadowBlur = 14;
      nodes.forEach(n => {
        const pulse = 0.6 + Math.sin(t * 0.00075 + n.glowPhase) * 0.4;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(96,165,250,${(0.32 * pulse).toFixed(2)})`;
        ctx.fill();
      });
      ctx.restore();

      ctx.save();
      ctx.shadowColor = "rgba(147,197,253,0.85)";
      ctx.shadowBlur = 10;
      nodes.forEach(n => {
        const pulse = 0.6 + Math.sin(t * 0.00075 + n.glowPhase) * 0.4;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(147,197,253,${pulse.toFixed(3)})`;
        ctx.fill();
      });
      ctx.restore();

      raf = requestAnimationFrame(frame);
    };

    startLoop();

    return () => {
      stopLoop();
      observer.disconnect();
    };
  }, []);

  return (
    <div className={className} aria-hidden="true">
      <canvas ref={canvasRef} className="hero-brain__canvas" />
    </div>
  );
};

export default BrainViz;
